import React, {
  ReactNode,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { createPortal } from 'react-dom';

interface TooltipProps {
  content: string | ReactNode;
  children: ReactNode;
  delay?: number;
  side?: 'top' | 'bottom' | 'left' | 'right';
  dark?: boolean;
  align?: 'left' | 'center' | 'right';
  multiline?: boolean;
  /** Minimum width of the tooltip (forces tooltip to be at least this wide) */
  contentMinWidth?: number;
  /** Max width of the tooltip content (default 300). Use a larger value for long comma-separated lists. */
  contentMaxWidth?: number;
  /** Max height of the tooltip content; when set, content scrolls vertically (e.g. for 1000+ items). */
  contentMaxHeight?: number;
  /** Explicitly controls whether the tooltip is shown on hover, overriding truncation detection entirely (e.g. permission-gated actions pass `!hasPermission`). Omit to fall back to truncation-based auto-detection. */
  forceShow?: boolean;
  /** When true and contentMaxWidth is set, use contentMaxWidth for tooltip width instead of trigger width (e.g. for "+N więcej" plan names). */
  useContentWidth?: boolean;
  /** When true, trigger span only wraps content (no w-full), so position is based on the actual trigger element (e.g. "+N więcej" link). */
  triggerWrapContent?: boolean;
  /** When true, renders the trigger as inline-block so it shrinks to the width of its text content. Use for side tooltips on menu items where the trigger should not span the full container width. */
  inlineTrigger?: boolean;
  /** Override the tabIndex on the trigger span. Pass -1 when the child is already a focusable element (e.g. a button) to avoid double tab stops. */
  triggerTabIndex?: number;
  /** Optional container ref for absolutely-positioned parents. When set, tooltip is positioned relative to this container (absolute positioning) instead of the viewport (fixed positioning). Use for tooltips within dropdowns or other absolutely-positioned containers. */
  containerRef?: { current: HTMLElement | null } | undefined;
  /** Override the cursor on the trigger wrapper. Use 'not-allowed' for disabled items that still show a tooltip. */
  cursor?: React.CSSProperties['cursor'];
  /** Override the tooltip's stacking z-index (default 9999). Lower it when the trigger sits near another high-z-index floating element (e.g. a calendar popover) that should be able to render above the tooltip. */
  zIndex?: number;
}

export function Tooltip({
  content,
  children,
  delay = 200, // Updated default delay to 200ms to fix tooltip getting stuck
  side = 'bottom',
  dark = true,
  align = 'left',
  contentMinWidth,
  contentMaxWidth = 300,
  contentMaxHeight,
  forceShow,
  useContentWidth = false,
  triggerWrapContent = false,
  inlineTrigger = false,
  triggerTabIndex,
  containerRef,
  cursor,
  zIndex = 9999,
}: TooltipProps) {
  const [show, setShow] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState<{
    top: number;
    left: number;
    position: 'top' | 'bottom' | 'left' | 'right';
    width: number | string;
  }>({
    top: 0,
    left: 0,
    position: 'bottom',
    width: 'auto',
  });
  const [isPositioned, setIsPositioned] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const isTextTruncated = () => {
    if (!triggerRef.current) {
      return false;
    }

    // Check if the child element has truncation applied (Tailwind `truncate` or custom `text-ellipsis`)
    const childElement = triggerRef.current.querySelector(
      '.truncate, [class*="text-ellipsis"]'
    );
    if (!childElement) {
      return true;
    } // Show tooltip if no ellipsis class found (backward compatible)

    // Check if content is actually truncated
    return childElement.scrollWidth > childElement.clientWidth;
  };

  const checkTruncation = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }
    const childElement = triggerRef.current.querySelector(
      '.truncate, [class*="text-ellipsis"]'
    );
    if (!childElement) {
      return;
    }
    setIsTruncated(childElement.scrollWidth > childElement.clientWidth);
  }, []);

  const calculatePosition = useCallback(() => {
    if (!triggerRef.current) {
      return;
    }

    // Always use trigger element's position - it's more reliable
    const rect = triggerRef.current.getBoundingClientRect();

    // Handle container-relative positioning for absolutely-positioned parents (e.g., dropdown menus)
    // Verify container still exists in DOM to prevent stale positioning in Safari
    const containerRect = containerRef?.current?.getBoundingClientRect();
    const isContainerPositioned =
      containerRef &&
      containerRef.current &&
      containerRect &&
      document.body.contains(containerRef.current);

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const padding = isContainerPositioned ? 4 : 8; // Tighter padding for container-relative positioning
    const maxWidth = Math.min(
      contentMaxWidth,
      (isContainerPositioned ? containerRect.width : viewportWidth) -
        padding * 2
    );
    const triggerWidth = rect.width;

    // Minimal gap for right-side tooltips to appear close to text
    const gap =
      isContainerPositioned && side === 'right'
        ? 2
        : isContainerPositioned
          ? 4
          : 12;

    // Use actual rendered dimensions when available (second pass after tooltip mounts)
    const rendered = tooltipRef.current?.getBoundingClientRect();
    const tooltipHeight = rendered?.height || 32;

    // For left/right: use actual rendered width so bubble fits content, not trigger
    // Round tooltip dimensions to avoid Safari floating-point precision issues
    let tooltipWidth: number;
    if (side === 'left' || side === 'right') {
      // Use actual rendered width, or default to contentMaxWidth (allow extending outside container)
      tooltipWidth = Math.round(
        rendered?.width || Math.min(contentMaxWidth, maxWidth)
      );
    } else {
      tooltipWidth = Math.round(
        useContentWidth && contentMaxWidth
          ? rendered?.width || maxWidth
          : Math.min(triggerWidth, maxWidth)
      );
    }

    // Apply contentMinWidth if provided
    if (contentMinWidth) {
      tooltipWidth = Math.max(
        tooltipWidth,
        Math.min(contentMinWidth, maxWidth)
      );
    }
    const effectiveAlign =
      useContentWidth && contentMaxWidth ? 'center' : align;

    // Calculate trigger position relative to container if containerRef is provided
    // Round all values to prevent floating-point precision issues in Safari
    let triggerRelativeRect: {
      top: number;
      left: number;
      bottom: number;
      right: number;
      height: number;
      width: number;
    } = {
      ...rect,
      top: Math.round(rect.top),
      left: Math.round(rect.left),
      bottom: Math.round(rect.bottom),
      right: Math.round(rect.right),
      height: Math.round(rect.height),
      width: Math.round(rect.width),
    };

    if (isContainerPositioned && containerRect) {
      // Create a rect relative to the container
      triggerRelativeRect = {
        ...triggerRelativeRect,
        top: Math.round(rect.top - containerRect.top),
        left: Math.round(rect.left - containerRect.left),
        bottom: Math.round(rect.bottom - containerRect.top),
        right: Math.round(rect.right - containerRect.left),
      };
    }

    let top: number;
    let left: number;
    let pos: 'top' | 'bottom' | 'left' | 'right' = 'bottom';

    if (side === 'left') {
      left =
        triggerRelativeRect.left -
        (typeof tooltipWidth === 'number' ? tooltipWidth : triggerWidth) -
        gap;
      const h = tooltipHeight;
      if (effectiveAlign === 'left') {
        top = triggerRelativeRect.top;
      } else if (effectiveAlign === 'right') {
        top = triggerRelativeRect.bottom - h;
      } else {
        top = triggerRelativeRect.top + (triggerRelativeRect.height - h) / 2;
      }
      pos = 'left';
    } else if (side === 'right') {
      // Position tooltip to the right of trigger element
      // For container-relative positioning, allow tooltip to extend outside container bounds
      left = triggerRelativeRect.right + gap;
      // For container-relative positioning, always use vertical centering; otherwise use alignment
      if (isContainerPositioned && containerRect) {
        top =
          triggerRelativeRect.top +
          triggerRelativeRect.height / 2 -
          tooltipHeight / 2;
      } else {
        const h = tooltipHeight;
        if (effectiveAlign === 'left') {
          top = triggerRelativeRect.top;
        } else if (effectiveAlign === 'right') {
          top = triggerRelativeRect.bottom - h;
        } else {
          top = triggerRelativeRect.top + (triggerRelativeRect.height - h) / 2;
        }
      }
      pos = 'right';
    } else {
      // top / bottom
      top = triggerRelativeRect.bottom + 2;
      pos = 'bottom';
      switch (effectiveAlign) {
        case 'right':
          left = triggerRelativeRect.right - tooltipWidth;
          break;
        case 'center':
          left = triggerRelativeRect.left + triggerWidth / 2 - tooltipWidth / 2;
          break;
        case 'left':
        default:
          left = triggerRelativeRect.left;
          break;
      }
      if (
        side === 'bottom' &&
        triggerRelativeRect.bottom + tooltipHeight + 2 >
          (isContainerPositioned && containerRect
            ? viewportHeight - containerRect.top
            : viewportHeight)
      ) {
        top = triggerRelativeRect.top - tooltipHeight - 2;
        pos = 'top';
      } else if (side === 'top') {
        top = triggerRelativeRect.top - tooltipHeight - 2;
        pos = 'top';
      }
    }

    // Clamp to viewport or container bounds
    const boundsWidth =
      isContainerPositioned && containerRect
        ? containerRect.width
        : viewportWidth;
    const boundsHeight =
      isContainerPositioned && containerRect
        ? containerRect.height
        : viewportHeight;

    let finalLeft = left;
    let finalWidth = tooltipWidth;

    // Vertical clamping always applies
    // Horizontal clamping: for container-relative right-side tooltips, allow extending outside
    if (!(isContainerPositioned && side === 'right')) {
      // For other cases, apply standard horizontal clamping
      finalWidth = Math.min(tooltipWidth, boundsWidth - padding * 2);
      const rightPadding =
        side === 'right' ? Math.max(padding * 3, 12) : padding;
      if (finalLeft < padding) {
        finalLeft = padding;
      }
      if (finalLeft + finalWidth > boundsWidth - rightPadding) {
        finalLeft = boundsWidth - finalWidth - rightPadding;
      }
    }

    if (side === 'left' || side === 'right') {
      let finalTop = top;
      if (finalTop < padding) {
        finalTop = padding;
      }
      if (finalTop + tooltipHeight > boundsHeight - padding) {
        finalTop = boundsHeight - tooltipHeight - padding;
      }
      // Container-relative right tooltips skip standard clamping (to let them
      // extend outside the container), but still must not overflow the viewport.
      if (isContainerPositioned && side === 'right' && containerRect) {
        const vpLeft = finalLeft + containerRect.left;
        if (vpLeft + finalWidth > viewportWidth - padding) {
          finalLeft = viewportWidth - containerRect.left - finalWidth - padding;
        }
        if (finalLeft + containerRect.left < padding) {
          finalLeft = padding - containerRect.left;
        }
      }
      setTooltipPosition({
        top: finalTop,
        left: finalLeft,
        position: pos,
        width: finalWidth,
      });
    } else {
      const tooltipRight = finalLeft + finalWidth;
      if (tooltipRight > boundsWidth - padding) {
        finalLeft = boundsWidth - finalWidth - padding;
      }
      if (effectiveAlign !== 'left' && finalLeft < padding) {
        finalLeft = padding;
      }
      let finalTop = top;
      const effectiveBottomBound =
        isContainerPositioned && containerRect
          ? viewportHeight - containerRect.top
          : boundsHeight;
      if (finalTop + tooltipHeight > effectiveBottomBound - padding) {
        finalTop = effectiveBottomBound - tooltipHeight - padding;
      }
      if (finalTop < padding) {
        finalTop = padding;
      }
      setTooltipPosition({
        top: finalTop,
        left: finalLeft,
        position: pos,
        width: useContentWidth && !rendered ? 'auto' : finalWidth,
      });
    }
  }, [
    side,
    align,
    contentMinWidth,
    contentMaxWidth,
    useContentWidth,
    containerRef,
  ]);

  // Use ResizeObserver to track truncation — catches window resize, zoom, and container-level layout changes
  useEffect(() => {
    const el = triggerRef.current;
    if (!el) {
      return;
    }

    checkTruncation();

    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);

    return () => observer.disconnect();
  }, [checkTruncation]);

  const handleMouseEnter = () => {
    // When forceShow is explicitly set (true or false), it takes full control
    // of visibility — e.g. permission-gated actions rely on forceShow={false}
    // to suppress the tooltip even though the wrapped content isn't truncatable
    // text. Only fall back to truncation detection when forceShow isn't passed.
    const shouldShow = forceShow !== undefined ? forceShow : isTextTruncated();
    if (!shouldShow) {
      return;
    }

    // Clear any pending timeout first
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      // Validate trigger element still exists and is being hovered
      if (triggerRef.current?.matches(':hover')) {
        // Check if container still exists in DOM (prevents stale tooltips in Safari)
        if (
          containerRef?.current &&
          !document.body.contains(containerRef.current)
        ) {
          return;
        }
        calculatePosition();
        setShow(true);
      }
    }, delay);
  };

  const handleMouseLeave = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setShow(false);
    setIsPositioned(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShow(false);
    }
  };

  useEffect(() => {
    if (show) {
      // If using container positioning, verify container still exists in DOM
      // This prevents Safari from rendering stale tooltips when container is removed
      if (
        containerRef?.current &&
        !document.body.contains(containerRef.current)
      ) {
        setShow(false);
        return;
      }

      // Recalculate position after tooltip is rendered to account for actual dimensions,
      // then mark as positioned so the tooltip becomes visible at its correct location.
      const timer = setTimeout(() => {
        calculatePosition();
        setIsPositioned(true);
      }, 0);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [show, content, calculatePosition, containerRef]);

  useEffect(() => {
    const handleScroll = () => {
      if (show && triggerRef.current) {
        // Only hide if trigger element is no longer in viewport or container bounds
        const rect = triggerRef.current.getBoundingClientRect();
        const containerRect = containerRef?.current?.getBoundingClientRect();

        let isInViewport: boolean;
        if (containerRef && containerRect) {
          // For container-relative positioning, check if trigger is within container bounds
          isInViewport =
            rect.top >= containerRect.top &&
            rect.left >= containerRect.left &&
            rect.bottom <= containerRect.bottom &&
            rect.right <= containerRect.right;
        } else {
          // For viewport-relative positioning, check if trigger is in viewport
          isInViewport =
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= window.innerHeight &&
            rect.right <= window.innerWidth;
        }

        if (!isInViewport) {
          setShow(false);
        } else {
          // Recalculate position on scroll to keep tooltip aligned
          calculatePosition();
        }
      }
    };

    const handleResize = () => {
      if (show) {
        calculatePosition();
      }
    };

    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    // For container-relative positioning, also listen to container scroll
    const containerEl = containerRef?.current;
    if (containerEl) {
      containerEl.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
      if (containerEl) {
        containerEl.removeEventListener('scroll', handleScroll, true);
      }
    };
  }, [show, calculatePosition, containerRef]);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={handleMouseEnter}
        onBlur={handleMouseLeave}
        onKeyDown={handleKeyDown}
        className={`outline-none ${inlineTrigger ? 'inline-block' : triggerWrapContent ? '' : 'w-full'}`}
        style={{
          cursor: cursor ?? (forceShow || isTruncated ? 'pointer' : 'inherit'),
        }}
        role={forceShow || isTruncated ? 'button' : undefined}
        tabIndex={
          triggerTabIndex !== undefined
            ? triggerTabIndex
            : forceShow || isTruncated
              ? 0
              : undefined
        }
      >
        {children}
      </div>
      {show &&
        (() => {
          // For container-relative positioning, convert to viewport coordinates
          const containerRect = containerRef?.current?.getBoundingClientRect();
          const isContainerMode =
            containerRef && containerRef.current && containerRect;
          const finalTop = isContainerMode
            ? Math.round(tooltipPosition.top + (containerRect?.top || 0))
            : Math.round(tooltipPosition.top);
          const finalLeft = isContainerMode
            ? Math.round(tooltipPosition.left + (containerRect?.left || 0))
            : Math.round(tooltipPosition.left);

          return createPortal(
            <div
              ref={tooltipRef}
              className={`fixed pointer-events-none inline-flex animate-fade-in`}
              style={{
                visibility: isPositioned ? 'visible' : 'hidden',
                flexDirection:
                  tooltipPosition.position === 'left' ||
                  tooltipPosition.position === 'right'
                    ? 'row'
                    : tooltipPosition.position === 'top'
                      ? 'column-reverse'
                      : 'column',
                alignItems:
                  tooltipPosition.position === 'left' ||
                  tooltipPosition.position === 'right'
                    ? 'center'
                    : 'center',
                top: `${finalTop}px`,
                left: `${finalLeft}px`,
                width:
                  tooltipPosition.position === 'left' ||
                  tooltipPosition.position === 'right'
                    ? undefined
                    : typeof tooltipPosition.width === 'number'
                      ? `${Math.round(tooltipPosition.width)}px`
                      : tooltipPosition.width,
                maxWidth: 'calc(100vw - 16px)',
                zIndex,
                willChange: 'transform',
              }}
            >
              {/* Arrow before content for top and right */}
              {(tooltipPosition.position === 'top' ||
                tooltipPosition.position === 'bottom') && (
                <svg
                  width="16"
                  height="8"
                  viewBox="0 0 16 8"
                  fill="none"
                  style={{
                    filter: dark
                      ? 'none'
                      : 'drop-shadow(0px -2px 3px rgba(0, 0, 0, 0.15))',
                    transform:
                      tooltipPosition.position === 'top'
                        ? 'rotate(180deg)'
                        : 'none',
                    flexShrink: 0,
                  }}
                >
                  <path
                    d="M0 8L16 8L9.41421 1.41421C8.63317 0.633166 7.36684 0.633166 6.58579 1.41421L0 8Z"
                    fill={dark ? '#181A1B' : '#FFFFFF'}
                  />
                </svg>
              )}
              {tooltipPosition.position === 'right' && (
                <svg
                  width="8"
                  height="16"
                  viewBox="0 0 8 16"
                  fill="none"
                  style={{
                    filter: dark
                      ? 'none'
                      : 'drop-shadow(-2px 0 3px rgba(0, 0, 0, 0.15))',
                    flexShrink: 0,
                    marginRight: '-1px',
                  }}
                >
                  <path
                    d="M8 0L8 16L0 8L8 0Z"
                    fill={dark ? '#181A1B' : '#FFFFFF'}
                  />
                </svg>
              )}
              {/* Tooltip Content */}
              <div
                className="inline-flex flex-col justify-start items-center text-center"
                style={{
                  padding: '8px',
                  width:
                    tooltipPosition.position === 'left' ||
                    tooltipPosition.position === 'right' ||
                    (useContentWidth &&
                      typeof tooltipPosition.width !== 'number')
                      ? 'fit-content'
                      : '100%',
                  maxWidth:
                    tooltipPosition.position === 'left' ||
                    tooltipPosition.position === 'right' ||
                    (useContentWidth &&
                      typeof tooltipPosition.width !== 'number')
                      ? `${contentMaxWidth}px`
                      : undefined,
                  // Prevent character-level breaking for side tooltips; use word breaking only
                  whiteSpace:
                    tooltipPosition.position === 'left' ||
                    tooltipPosition.position === 'right' ||
                    (useContentWidth &&
                      typeof tooltipPosition.width !== 'number')
                      ? 'nowrap'
                      : 'normal',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  overflowX: 'hidden',
                  overflowY: contentMaxHeight ? 'auto' : 'hidden',
                  maxHeight: contentMaxHeight,
                  borderRadius: '4px',
                  color: dark ? '#FFFFFF' : '#181A1B',
                  backgroundColor: dark ? '#181A1B' : '#FFFFFF',
                  fontSize: '12px',
                  fontFamily: 'Roboto',
                  fontWeight: '400',
                  lineHeight: '16px',
                  letterSpacing: '0.4px',
                  boxShadow: dark
                    ? 'none'
                    : '0px 2px 3px rgba(0, 0, 0, 0.15), 0px 6px 10px 4px rgba(0, 0, 0, 0.08), 0px -2px 4px rgba(0, 0, 0, 0.15)',
                }}
              >
                {typeof content === 'string'
                  ? content.split('\n').map((line, index) => (
                      <span key={index}>
                        {line}
                        {index < content.split('\n').length - 1 && <br />}
                      </span>
                    ))
                  : content}
              </div>
              {/* Arrow on right for position left (points right) */}
              {tooltipPosition.position === 'left' && (
                <svg
                  width="8"
                  height="16"
                  viewBox="0 0 8 16"
                  fill="none"
                  style={{
                    filter: dark
                      ? 'none'
                      : 'drop-shadow(2px 0 3px rgba(0, 0, 0, 0.15))',
                    flexShrink: 0,
                    marginLeft: '-1px',
                  }}
                >
                  <path
                    d="M0 0L0 16L8 8L0 0Z"
                    fill={dark ? '#181A1B' : '#FFFFFF'}
                  />
                </svg>
              )}
            </div>,
            document.body // Always render to document.body to allow extending outside containers
          );
        })()}
    </>
  );
}
